import './style.css';
import { Canvas2DRenderer } from './render/canvas2d-renderer';
import { Engine } from './core/engine';
import { affectModules, createModules } from './modules/registry';
import { attachPointer } from './input/pointer';
import { buildToolbar } from './ui/toolbar';
import { buildLibraryPanel } from './ui/library-panel';
import { ShapeLibrary } from './core/shape-library';
import { syncStampSetOptions } from './modules/create/stamp';
import { exportPngFile, exportSvg, exportSvgFile } from './export/exporters';

const app = document.getElementById('app');
if (!app) throw new Error('#app not found');

// Embed mode (?embed): a chrome-free canvas for the portfolio's "Draw
// something." section. The toolbar/library are hidden, tools are preset to a
// friendly default, and a minimal Undo / Start-over bar is the only UI.
const embed = new URLSearchParams(location.search).has('embed');
if (embed) document.documentElement.classList.add('alc-embed');

const canvas = document.createElement('canvas');
canvas.className = 'alc-canvas';
app.appendChild(canvas);

const dprCap = 2.5;
const dpr = () => Math.min(window.devicePixelRatio || 1, dprCap);
// Fall back to sane dimensions if the viewport reports 0 before first layout,
// so the scene never initialises at 0×0 (which collapses drawings to the origin).
const vw = () => window.innerWidth || document.documentElement.clientWidth || 1024;
const vh = () => window.innerHeight || document.documentElement.clientHeight || 768;

const renderer = new Canvas2DRenderer(canvas);
const lib = new ShapeLibrary();
const creates = createModules(lib);
const affects = affectModules();
const engine = new Engine(renderer, creates[0], vw(), vh(), dpr());
// Register every module's defaults up front so setParam works before that
// module is ever selected (e.g. setting the Stamp set from the library panel).
for (const m of [...creates, ...affects]) engine.ensureConfig(m.manifest);

attachPointer(canvas, engine);

// Trace image: a hidden file input + a loader that sets it and switches to the Trace tool.
const traceModule = creates.find((m) => m.manifest.id === 'trace');
const traceFileInput = document.createElement('input');
traceFileInput.type = 'file';
traceFileInput.accept = 'image/*';
traceFileInput.style.display = 'none';
app.appendChild(traceFileInput);
const loadTraceImage = (file: File): void => {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string') {
      engine.setTraceImage(reader.result);
      if (traceModule) engine.setCreate(traceModule);
    }
  };
  reader.onerror = () => console.warn('Alchemy: could not read the trace image file');
  reader.readAsDataURL(file);
};
traceFileInput.addEventListener('change', () => {
  const f = traceFileInput.files?.[0];
  if (f) loadTraceImage(f);
  traceFileInput.value = '';
});

const libPanel = buildLibraryPanel(lib, engine);
app.appendChild(libPanel.el);
app.appendChild(
  buildToolbar(engine, renderer, creates, affects, () => libPanel.open(), () => traceFileInput.click()),
);

// A floating "clear trace" control, shown only while a trace image is loaded.
const clearTraceBtn = document.createElement('button');
clearTraceBtn.type = 'button';
clearTraceBtn.className = 'alc-trace-clear';
clearTraceBtn.textContent = 'Clear trace image';
clearTraceBtn.hidden = true;
clearTraceBtn.addEventListener('click', () => engine.setTraceImage(null));
app.appendChild(clearTraceBtn);
const toolbarOnChange = engine.onChange;
engine.onChange = () => {
  toolbarOnChange?.();
  clearTraceBtn.hidden = !engine.getTraceImage();
};

// Keep the Stamp tool's set dropdown in sync as sets are imported or removed.
const stampModule = creates.find((m) => m.manifest.id === 'stamp');
lib.onChange = () => {
  if (stampModule) syncStampSetOptions(stampModule.manifest, lib);
  // Repair a dangling Stamp set selection (e.g. the active set was deleted).
  const cur = engine.getConfig('stamp').set;
  if (typeof cur !== 'string' || !lib.get(cur)) engine.setParam('stamp', 'set', lib.firstId());
  libPanel.refresh();
  engine.onChange?.();
};

// Drop an SVG anywhere on the canvas to import it as a new set.
app.addEventListener('dragover', (e) => e.preventDefault());
app.addEventListener('drop', (e) => {
  e.preventDefault();
  const files = e.dataTransfer?.files;
  if (!files || files.length === 0) return;
  const list = Array.from(files);
  const isRaster = (f: File): boolean =>
    (f.type.startsWith('image/') && f.type !== 'image/svg+xml') ||
    (f.type === '' && /\.(png|jpe?g|gif|webp|bmp|avif)$/i.test(f.name));
  const image = list.find(isRaster);
  const hasSvg = list.some((f) => /\.svg$/i.test(f.name) || f.type === 'image/svg+xml');
  if (image) loadTraceImage(image);
  if (hasSvg) {
    libPanel.open();
    void libPanel.importFiles(files);
  }
});

window.addEventListener('resize', () => {
  engine.resize(vw(), vh(), dpr());
});

window.addEventListener('keydown', (e) => {
  const mod = e.metaKey || e.ctrlKey;
  if (mod && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    engine.undo();
  } else if (mod && e.shiftKey && e.key.toLowerCase() === 'p') {
    e.preventDefault();
    exportPngFile(renderer, engine.scene, 2);
  } else if (mod && e.shiftKey && e.key.toLowerCase() === 's') {
    e.preventDefault();
    exportSvgFile(engine.scene);
  }
});

// Embed always opens onto a blank page (no session restore) with a curated
// preset — matched to Yoel's reference setup: Shapes (light weight) through
// Repeat (radial echoes) + Smooth.
void Promise.allSettled([embed ? Promise.resolve() : engine.restore(), lib.load()]).then(() => {
  if (embed) {
    const shapes = creates.find((m) => m.manifest.id === 'shapes');
    if (shapes) engine.setCreate(shapes);
    for (const id of ['repeat', 'smooth']) {
      const m = affects.find((a) => a.manifest.id === id);
      if (m && !engine.isAffectActive(m)) engine.toggleAffect(m);
    }
    engine.setParam('shapes', 'lineWidth', 3);
    engine.setParam('repeat', 'count', 7);
  }
  engine.start();
});

if (embed) {
  // Minimal chrome: Undo + Start over, floating over the canvas.
  const bar = document.createElement('div');
  bar.className = 'alc-embed-bar';
  const mkBtn = (label: string, onClick: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'alc-embed-btn';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  };
  bar.append(
    mkBtn('Undo', () => engine.undo()),
    mkBtn('Start over', () => engine.clear()),
  );
  app.appendChild(bar);

  // A full-viewport iframe swallows wheel events, which would trap the parent
  // page's scroll. Hand them up: feed the parent's Lenis (synthetic wheel
  // bubbles to its window listener) or fall back to native scrollBy.
  if (window.self !== window.top) {
    window.addEventListener(
      'wheel',
      (e) => {
        try {
          const pw = window.parent as Window & { __lenis?: unknown };
          if (pw.__lenis) {
            pw.document.documentElement.dispatchEvent(
              new WheelEvent('wheel', {
                deltaY: e.deltaY,
                deltaX: e.deltaX,
                deltaMode: e.deltaMode,
                bubbles: true,
                cancelable: true,
              }),
            );
          } else {
            pw.scrollBy(0, e.deltaY);
          }
        } catch {
          /* cross-origin parent — leave the event alone */
        }
      },
      { passive: true },
    );
  }
}

// Register the service worker for offline use / installability (production only,
// so it never interferes with the dev server's hot-module reloading). Skipped
// inside the Tauri desktop shell — its assets are already bundled locally, and a
// SW cache would only risk serving stale content after an app update.
const inTauri = '__TAURI_INTERNALS__' in window;
if ('serviceWorker' in navigator && (import.meta as { env?: { PROD?: boolean } }).env?.PROD && !inTauri) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

// Dev-only introspection hook (stripped from production builds).
if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
  (window as unknown as { __alc: unknown }).__alc = { engine, renderer, creates, affects, lib, libPanel, exportSvg };
}
